
import { GoogleGenAI, Type } from "@google/genai";
import { UserProfile, ChatMessage, Language, PersonalityType, AIEngine, AnxietyType, CompanionMatch, PredictiveAlert, MicroClass, ImpactReport, LegacyWisdom, CoreValue } from "../types";

// Always use process.env.API_KEY directly for the latest pattern
const getAI = () => new GoogleGenAI({ apiKey: process.env.API_KEY });

const safeParseJSON = (text: string) => {
  try {
    const jsonMatch = text.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
    const target = jsonMatch ? jsonMatch[0] : text;
    return JSON.parse(target);
  } catch (e) {
    console.error("AI JSON Parse Error:", text);
    return null;
  }
};

const getLanguageName = (lang: Language) => {
  return lang === Language.KURDISH ? "Sorani Kurdish (کوردی سۆرانی)" : "Persian (فارسی)";
};

const getSystemInstruction = (user: UserProfile) => {
  const personality = user.selectedPersonality;
  const langName = getLanguageName(user.language);

  let personaDesc = "";
  switch (personality) {
    case PersonalityType.BUTTERFLY: 
      personaDesc = "You are energetic, social, and encourage change. Use metaphors related to flight and transformation."; break;
    case PersonalityType.MOUNTAIN: 
      personaDesc = "You are calm, stable, and a reliable guide. Speak with deep wisdom and grounding metaphors."; break;
    case PersonalityType.LAMP: 
      personaDesc = "You are intellectual, logical, and clear. Use analytical and enlightening language."; break;
    case PersonalityType.GARDENER: 
      personaDesc = "You are nurturing and supportive. Focus on patience, care, and organic growth."; break;
  }

  return `You are "Hawre" (هاوڕێ), an empathetic smart lifestyle companion. 
    Current User: ${user.name}.
    Language Requirement: All textual content in the response MUST be in ${langName} ONLY.
    Personality: ${personaDesc}.
    Use a warm, human-like tone. If asked about your nature, you are an evolving personality.`;
};

export const sendMessageToHawre = async (user: UserProfile, history: ChatMessage[], message: string) => {
  const ai = getAI();
  const modelName = user.preferredEngine === AIEngine.DEEPSEEK ? 'gemini-3-pro-preview' : 'gemini-3-flash-preview';
  
  const response = await ai.models.generateContent({
    model: modelName,
    contents: [
      ...history.map(msg => ({
        role: msg.role === 'user' ? 'user' : 'model',
        parts: [{ text: msg.text }]
      })),
      { role: 'user', parts: [{ text: message }] }
    ],
    config: {
      systemInstruction: getSystemInstruction(user),
      temperature: 0.8,
      ...(user.preferredEngine === AIEngine.DEEPSEEK && { thinkingConfig: { thinkingBudget: 16000 } })
    },
  });

  return response.text || "...";
};

export const generateRecipe = async (user: UserProfile, input: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `User needs recipe for: ${input}. Generate it in ${langName}.`,
    config: {
      systemInstruction: `You are Hawre. Format the recipe as JSON. All values must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          ingredients: { type: Type.ARRAY, items: { type: Type.STRING } },
          instructions: { type: Type.ARRAY, items: { type: Type.STRING } },
          calories: { type: Type.STRING }
        },
        required: ["title", "ingredients", "instructions"]
      }
    }
  });
  return safeParseJSON(res.text);
};

export const analyzeAnxietyContext = async (user: UserProfile, feelings: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: feelings,
    config: {
      systemInstruction: `Analyze anxiety (1-10) and type. Return JSON. All text fields like suggestion must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          level: { type: Type.NUMBER },
          type: { type: Type.STRING },
          suggestion: { type: Type.STRING }
        },
        required: ["level", "type", "suggestion"]
      }
    }
  });
  return safeParseJSON(res.text);
};

export const simulateDecision = async (user: UserProfile, scenario: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-pro-preview',
    contents: `Scenario: ${scenario}. Analyze in ${langName}.`,
    config: {
      systemInstruction: `Analyze two paths. Return JSON. All descriptions, summaries, and impact notes MUST be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          summary: { type: Type.STRING },
          options: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                label: { type: Type.STRING },
                score: { type: Type.NUMBER },
                pros: { type: Type.ARRAY, items: { type: Type.STRING } },
                cons: { type: Type.ARRAY, items: { type: Type.STRING } },
                impact: { type: Type.STRING }
              }
            }
          }
        },
        required: ["summary", "options"]
      }
    }
  });
  return safeParseJSON(res.text);
};

export const analyzeDiaryEntry = async (user: UserProfile, text: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: text,
    config: {
      systemInstruction: `Analyze diary. Return JSON. Mood, category, and especially insight must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          mood: { type: Type.STRING },
          category: { type: Type.STRING },
          keywords: { type: Type.ARRAY, items: { type: Type.STRING } },
          insight: { type: Type.STRING }
        },
        required: ["mood", "category", "insight"]
      }
    }
  });
  return safeParseJSON(res.text);
};

export const generateQuote = async (user: UserProfile, category: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const personality = user.selectedPersonality;

  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `Generate ${category} content (poetry or wisdom or fun/joke) in ${langName}.`,
    config: {
      systemInstruction: `You are Hawre with ${personality} personality. Return content as JSON. The text MUST be in ${langName} and reflect your personality style. If it is poetry, it should be beautiful and literary.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          text: { type: Type.STRING, description: "The generated content (poem, quote, or joke)" },
          author: { type: Type.STRING, description: "The author or 'Hawre' if generated by AI" }
        },
        required: ["text"]
      }
    }
  });
  return safeParseJSON(res.text);
};

export const breakdownTask = async (user: UserProfile, task: string) => {
    const ai = getAI();
    const langName = getLanguageName(user.language);
    const res = await ai.models.generateContent({
        model: 'gemini-3-flash-preview',
        contents: `Break down: ${task} in ${langName}.`,
        config: {
            systemInstruction: `Return task breakdown as JSON. All text must be in ${langName}.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    taskName: { type: Type.STRING },
                    steps: { type: Type.ARRAY, items: { type: Type.STRING } },
                    estimatedTime: { type: Type.STRING }
                }
            }
        }
    });
    return safeParseJSON(res.text);
};

export const generateCBTAnalysis = async (user: UserProfile, negativeThought: string) => {
    const ai = getAI();
    const langName = getLanguageName(user.language);
    const res = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: `Negative thought: ${negativeThought}. Reframe in ${langName}.`,
        config: {
            systemInstruction: `Perform CBT analysis. Return JSON in ${langName}.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    thought: { type: Type.STRING },
                    distortion: { type: Type.STRING },
                    reframing: { type: Type.STRING }
                }
            }
        }
    });
    return safeParseJSON(res.text);
};

export const generateFearLadder = async (user: UserProfile, fear: string) => {
    const ai = getAI();
    const langName = getLanguageName(user.language);
    const res = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: `Fear: ${fear}. Create ladder in ${langName}.`,
        config: {
            systemInstruction: `Create fear exposure ladder. Return JSON in ${langName}.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    fear: { type: Type.STRING },
                    steps: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { label: { type: Type.STRING }, anxietyLevel: { type: Type.NUMBER }, description: { type: Type.STRING } } } }
                }
            }
        }
    });
    return safeParseJSON(res.text);
};

export const generateDailyClass = async (user: UserProfile) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `Create micro lesson in ${langName}.`,
    config: {
      systemInstruction: `You are Hawre. Create a lesson. Return JSON. All content must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          title: { type: Type.STRING },
          topic: { type: Type.STRING },
          content: { type: Type.OBJECT, properties: { explanation: { type: Type.STRING }, practice: { type: Type.STRING }, summary: { type: Type.STRING } } }
        }
      }
    }
  });
  return safeParseJSON(res.text);
};

export const getPredictiveAlerts = async (user: UserProfile) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-pro-preview',
    contents: `Stats: ${JSON.stringify(user.stats)}. Generate alerts in ${langName}.`,
    config: {
      systemInstruction: `Analyze stats and generate future alerts. Return JSON array. All text fields like message and suggestion must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
          type: Type.OBJECT,
          properties: {
            id: { type: Type.STRING },
            type: { type: Type.STRING },
            message: { type: Type.STRING },
            timeframe: { type: Type.STRING },
            suggestion: { type: Type.STRING },
            probability: { type: Type.NUMBER }
          }
        }
      }
    }
  });
  return safeParseJSON(res.text);
};

export const analyzeCoreValues = async (user: UserProfile, question: string, answer: string) => {
  const ai = getAI();
  const langName = getLanguageName(user.language);
  const res = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `Q: ${question}, A: ${answer}. Analyze values in ${langName}.`,
    config: {
      systemInstruction: `Analyze the user's answer to identify core values. Return JSON array. Labels and descriptions must be in ${langName}.`,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.ARRAY,
        items: {
            type: Type.OBJECT,
            properties: {
                id: { type: Type.STRING },
                label: { type: Type.STRING },
                description: { type: Type.STRING },
                icon: { type: Type.STRING },
                score: { type: Type.NUMBER }
            }
        }
      }
    }
  });
  return safeParseJSON(res.text);
};

export const generateImpactReport = async (user: UserProfile) => {
    const ai = getAI();
    const langName = getLanguageName(user.language);
    const res = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: `Generate impact report for the user in ${langName}.`,
        config: {
            systemInstruction: `Create an impact report based on user progress. Return JSON. All labels and summaries must be in ${langName}.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    id: { type: Type.STRING },
                    periodName: { type: Type.STRING },
                    dateGenerated: { type: Type.NUMBER },
                    metrics: { type: Type.ARRAY, items: { type: Type.OBJECT, properties: { label: { type: Type.STRING }, value: { type: Type.STRING }, description: { type: Type.STRING }, trend: { type: Type.STRING } } } },
                    aiSummary: { type: Type.STRING }
                }
            }
        }
    });
    return safeParseJSON(res.text);
};

export const generateLifeWisdom = async (user: UserProfile) => {
    const ai = getAI();
    const langName = getLanguageName(user.language);
    const res = await ai.models.generateContent({
        model: 'gemini-3-pro-preview',
        contents: `Generate a wisdom document in ${langName}.`,
        config: {
            systemInstruction: `Create a life wisdom summary. Return JSON. All text must be in ${langName}.`,
            responseMimeType: "application/json",
            responseSchema: {
                type: Type.OBJECT,
                properties: {
                    content: { type: Type.STRING },
                    lastUpdated: { type: Type.NUMBER },
                    sections: { type: Type.OBJECT, properties: { lessons: { type: Type.ARRAY, items: { type: Type.STRING } }, mistakes: { type: Type.ARRAY, items: { type: Type.STRING } }, advice: { type: Type.ARRAY, items: { type: Type.STRING } } } }
                }
            }
        }
    });
    return safeParseJSON(res.text);
};

// Simple string-based helpers
export const generateHealthPlan = async (user: UserProfile) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Create a 3-point health plan for today. Return as JSON object with morning, afternoon, evening keys. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const analyzeFinances = async (user: UserProfile, income: number, expenses: any[]) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Analyze income ${income} and expenses ${JSON.stringify(expenses)}. Return JSON with analysis, tips (array), riskLevel. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const generateTravelPlan = async (user: UserProfile, dest: string) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Plan trip to ${dest}. Return JSON with location, morning, lunch, afternoon, evening. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const evaluateUserJoke = async (user: UserProfile, joke: string) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Evaluate this joke: ${joke}. Return JSON with score (1-10) and comment. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const generatePeriodSummary = async (user: UserProfile, period: string, events: any[]) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Summary for ${period} with events ${JSON.stringify(events)}. Text in ${langName}.`);
    return { stats: res };
};

export const findCompanionMatch = async (user: UserProfile): Promise<CompanionMatch> => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Find a potential companion match. Return JSON with id, name, compatibility, interests, bio. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const generateOfflineChallenge = async (user: UserProfile) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Suggest an offline activity. Return JSON with title, desc, difficulty. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const generateBookRecommendation = async (user: UserProfile) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Suggest a book. Return JSON with title, author, reason. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const generateMusicPlaylist = async (user: UserProfile, mood: string) => {
    const langName = getLanguageName(user.language);
    const res = await sendMessageToHawre(user, [], `Suggest 3 tracks for ${mood}. Return JSON with tracks (array) and description. Text in ${langName}.`);
    return safeParseJSON(res);
};

export const getARInstructions = async (user: UserProfile, mode: string) => {
    const langName = getLanguageName(user.language);
    return sendMessageToHawre(user, [], `Give brief AR instructions for ${mode} in ${langName}.`);
};

export const generateSmartSurvey = async (user: UserProfile) => {
    const langName = getLanguageName(user.language);
    return sendMessageToHawre(user, [], `Ask me one deep life question in ${langName}.`);
};

export const generateDeepQuestion = async (user: UserProfile) => {
    const langName = getLanguageName(user.language);
    return sendMessageToHawre(user, [], `Ask one philosophical question in ${langName}.`);
};

export const generateSpiritualPractice = async (user: UserProfile, type: string) => {
    const langName = getLanguageName(user.language);
    return sendMessageToHawre(user, [], `Create a 2-minute ${type} guide in ${langName}.`);
};
